import {
  getLicensedDocumentationImage,
  getLicensedDocumentationMarkdown,
} from "../../network/cipAPI";

const MARKDOWN_IMAGE_REGEX = /!\[\]\(([^)]+)\)/g;

const MARKDOWN_H2_H3_REGEX = /^(##|###) ([^\r\n]+)$/gm;

export const buildDocumentationHeaderId = (headerText) =>
  headerText
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "") // Remove all non-alphanumeric, non-space, non-hyphen characters
    .trim()
    .replace(/\s+/g, "-"); // Replace spaces with hyphens

const extractHeadersFromMarkdown = (markdownContent) => {
  if (!markdownContent) {
    return [];
  }

  const headersList = [];
  const matches = Array.from(markdownContent.matchAll(MARKDOWN_H2_H3_REGEX));
  matches.forEach((match) => {
    const level = match[1] === "###" ? 3 : 2;
    const title = match[2].trim();
    const id = buildDocumentationHeaderId(title);

    if (title && id) {
      headersList.push({ id, title, level });
    }
  });

  return headersList;
};

const blobToDataUrl = (blob) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });

const replaceMarkdownImagesWithApiData = async (markdownContent) => {
  const imageNames = Array.from(markdownContent.matchAll(MARKDOWN_IMAGE_REGEX))
    .map((match) => match[1].trim())
    .filter(Boolean);

  if (!imageNames.length) {
    return markdownContent;
  }

  const uniqueImageNames = [...new Set(imageNames)];
  const imageDataUrlByName = {};

  await Promise.all(
    uniqueImageNames.map(async (imageName) => {
      try {
        const response = await getLicensedDocumentationImage(imageName);

        if (!response || response.status !== 200) {
          return;
        }

        imageDataUrlByName[imageName] = await blobToDataUrl(response.data);
      } catch {
        // Keep markdown image source unchanged if image fetch fails.
      }
    }),
  );

  return markdownContent.replace(MARKDOWN_IMAGE_REGEX, (match, source) => {
    const imageName = source.trim();
    const imageDataUrl = imageDataUrlByName[imageName];

    if (!imageDataUrl) {
      return match;
    }

    return `![](${imageDataUrl})`;
  });
};

export const getLicensedDocumentationContent = async (selectedLicenseTabId) => {
  if (!selectedLicenseTabId) {
    throw new Error("No documentation mapping found for selected license");
  }

  const response = await getLicensedDocumentationMarkdown(selectedLicenseTabId);

  if (!response || response.status !== 200) {
    throw new Error("Failed to fetch documentation");
  }

  const headersList = extractHeadersFromMarkdown(response.data);
  const markdownWithResolvedImages = await replaceMarkdownImagesWithApiData(
    response.data,
  );

  return {
    markdownWithResolvedImages,
    headersList,
  };
};
